import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	TemplateRef,
	ViewChild,
} from '@angular/core';
import { CmNavigationService } from '@ats/cm/navigation';
import { AtsNotificationService } from '@ats/notifications';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, take, takeUntil, tap } from 'rxjs/operators';
import { CmTableColumn, CmTableConfig, CmTableGroup, CmTableRow, CmTableTools } from '../../models';
import { CmTableDatarow } from '../../models/cm-table-data-row.interface';
import { CmTableService } from '../../services/table.service';

@Component({
    selector: 'cm-table',
    templateUrl: './cm-table.component.html',
    styleUrls: ['./cm-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [CmTableService],
    standalone: false
})
export class CmTableComponent<Data = any> implements OnInit, OnDestroy {
	private ngUnsubscribe = new Subject<void>();
	private lastSelectedSubj$ = new BehaviorSubject<number>(null);
	@Input() minWidth = 45;
	@Input() minWidthUnit: string = 'rem';
	@Input() maxWidth: number;
	@Input() maxWidthUnit: string = 'rem';
	@Input() config$: Observable<CmTableConfig>;
	@Input() headerTemplate: TemplateRef<any>;
	@Input() rowTemplate: TemplateRef<any>;
	@Input() summaryTemplate: TemplateRef<any>;
	@Input() holdOpen;
	@Input() tools: CmTableTools[] = [
		CmTableTools.FILTER,
		CmTableTools.TOGGLE_FILTER,
		CmTableTools.EMPTY_GROUPS,
		CmTableTools.TOGGLE_GROUPS,
		CmTableTools.ZOOM,
		CmTableTools.FULLSCREEN,
		CmTableTools.DOWNLOAD,
	];
	@Output() toggledRow: EventEmitter<CmTableRow> = new EventEmitter<CmTableRow>();
	@ViewChild('filterInp') filterField: ElementRef;
	toolsEnum = CmTableTools;
	rows$: Observable<CmTableRow[]>;
	rowCount$: Observable<number>;
	columns$: Observable<CmTableColumn[]>;
	groups$: Observable<CmTableGroup[]>;
	summaries$: Observable<{ summary: any; id: string }[]>;
	showSummaries$: Observable<boolean>;
	sort$: Observable<string[]>;
	filter$ = new BehaviorSubject<string>('');
	isSelecting$: Observable<boolean>;
	selectable$: Observable<boolean>;

	levels: any[] = [];
	size = 0.75;
	anyNotFiltered = false;
	allOpen = false;
	fullscreen = false;
	previousNavState: boolean;
	navState: boolean;
	showEmptyGroups = false;
	anyEmptyGroups = true;
	gridStyles$: Observable<string>;
	isLoading$: Observable<boolean>;
	// public selectionSnapshot: { selected: any[]; unselected: any[] };
	public selection$: Observable<{ selected: Data[]; remaining: Data[] }>;
	public selectionWithMeta$: Observable<{ selected: CmTableDatarow[]; remaining: CmTableDatarow[] }>;

	constructor(
		private waiter: CmTableService,
		private notifications: AtsNotificationService,
		private navService: CmNavigationService
	) {}

	ngOnInit() {
		this.navService.navVisible$.pipe(distinctUntilChanged(), takeUntil(this.ngUnsubscribe)).subscribe(visible => {
			this.navState = visible;
		});
		this.isLoading$ = this.loadingStreams$();

		this.gridStyles$ = this.waiter.gridStyles$.pipe(
			distinctUntilChanged(),
			map((styles: string) => (styles && !!styles.length ? styles : '100%'))
		);

		this.selectable$ = this.waiter.isSelecting$.pipe(
			distinctUntilChanged(),
			map(isSelecting => isSelecting !== null)
		);

		this.isSelecting$ = this.waiter.isSelecting$.pipe(distinctUntilChanged());

		this.sort$ = this.waiter.sort$;

		this.groups$ = this.waiter.groups$;

		this.rows$ = this.waiter.rows$.pipe(
			map((rows: CmTableRow[]) => rows.filter(row => !row.filtered)),
			tap(
				(rows: CmTableRow[]) =>
					(this.allOpen = rows
						.filter(
							row => row.id !== 'summary' && !row.data && (!this.holdOpen || row.level > this.holdOpen)
						)
						.every(row => row.open))
			),
			tap(
				(rows: CmTableRow[]) =>
					(this.anyEmptyGroups = rows
						.filter(row => row.id !== 'summary' && !row.data)
						.some(row => row.childCount === 0))
			)
		);

		this.rowCount$ = this.rows$.pipe(map(rows => rows.filter(row => row.visible || row?.childCount !== 0).length));

		this.columns$ = this.waiter.columns$.pipe(
			tap((columns: CmTableColumn[]) => (this.anyNotFiltered = columns.some(col => !col.filtered))),
			map((columns: CmTableColumn[]) => columns.filter(col => !col.hidden))
		);

		const filter$ = this.filter$.asObservable().pipe(debounceTime(450), distinctUntilChanged());

		const config$ = this.config$;

		this.waiter.config(config$, filter$);

		this.selection$ = this.waiter.selection$.pipe(
			map(selection => ({
				selected: selection.selected.map(row => row.data),
				remaining: selection.remaining.map(row => row.data),
			}))
		);

		this.selectionWithMeta$ = this.waiter.selection$;
	}

	trackByFn = data => data.id;
	toggleSelection = (row: CmTableRow, idx?: number, ev?: MouseEvent) => {
		if (ev && ev.shiftKey && (idx || idx === 0)) {
			combineLatest([this.rows$, this.lastSelectedSubj$.asObservable()])
				.pipe(take(1))
				.subscribe(([rows, previous]) => {
					if (previous || previous === 0) {
						const previousRow = rows[previous];
						// Don't do multiple if it spans groups. That is messy and ugly.
						if (
							!(previousRow.parentGroup && row.parentGroup) ||
							previousRow.parentGroup === row.parentGroup
						) {
							// Positive range means first clicked item is lower in the table
							const start = previous - idx < 0 ? previous + 1 : idx;
							const end = previous - idx < 0 ? idx + 1 : previous;
							this.waiter.toggleSelection(rows.slice(start, end));
						} else {
							this.waiter.toggleSelection(row);
						}
					}
				});
		} else {
			this.waiter.toggleSelection(row);
		}
		if (idx || idx === 0) this.lastSelectedSubj$.next(idx);
	};
	clearSelection = () => this.waiter.clearSelection();

	toggleGroup = groupid => this.waiter.toggleGroup(groupid);
	toggleAllGroups = () => this.waiter.toggleAllGroups(this.holdOpen);
	toggleSort = (columnId, sortable?) => {
		if (sortable !== false) this.waiter.toggleSort(columnId);
	};
	toggleFilter = columnId => this.waiter.toggleFilter(columnId);
	toggleAllFilters = () => this.waiter.toggleAllFilters();
	toggleEmptyGroups = () => (this.showEmptyGroups = !this.showEmptyGroups);
	filter = value => this.filter$.next(value);

	toggleFullscreen = () => {
		if (!this.fullscreen) {
			this.previousNavState = this.navState;
			if (this.navState) {
				this.navService.toggleNav();
			}
			this.fullscreen = true;
		} else {
			if (this.previousNavState) this.navService.toggleNav();
			this.fullscreen = false;
		}
	};
	clearFilter = () => {
		this.filter('');
		this.filterField.nativeElement.value = '';
	};
	zoom = (direction: string) => {
		switch (direction) {
			case 'out':
				this.size -= 0.25;
				break;
			case 'in':
				this.size += 0.25;
				break;
			default:
				this.size = 0.75;
				break;
		}
	};

	ngOnDestroy() {
		this.ngUnsubscribe.next();
		this.ngUnsubscribe.complete();
	}

	// stop table loader from showing if the page loader is visible
	loadingStreams$(): Observable<boolean> {
		const waiterLoading$ = this.waiter.isLoading$.pipe(startWith(true));
		const notificationsLoading$ = this.notifications.loading$;
		return combineLatest([waiterLoading$, notificationsLoading$]).pipe(
			map(([serviceLoader, pageLoader]) => (pageLoader ? false : serviceLoader))
		);
	}
}
